import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import ClassOfferingsPanel from "./ClassOfferingsPanel";
import SubjectsByGradePanel from "./SubjectsByGradePanel";
import SectionsByGradePanel from "./SectionsByGradePanel";
import RoomsPanel from "./RoomsPanel";
import RoomPicker from "../../components/RoomPicker";
import DatePicker from "../../components/DatePicker";
import Select from "../../components/Select";
import s from "./AcademicsPage.module.css";

/** Adds a `.scrolling` class while the user is actively scrolling; fades it
 * off ~700ms after the last scroll event. Used to reveal the scrollbar on
 * swipe/scroll and auto-hide when idle — matches native macOS/iOS behavior. */
function useScrollActiveClass(activeClass) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let t;
    const onScroll = () => {
      el.classList.add(activeClass);
      clearTimeout(t);
      t = setTimeout(() => el.classList.remove(activeClass), 700);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      el.removeEventListener("scroll", onScroll);
    };
  }, [activeClass]);
  return ref;
}

/* ---------- Tab definitions ---------- */
const TABS = [
  { key: "school-years",    label: "School Years",    desc: "Define each academic year your school runs."             },
  { key: "semesters",       label: "Semesters",       desc: "Terms within a school year (1st / 2nd)."                  },
  { key: "departments",     label: "Departments",     desc: "Organisational units — academic levels and staff functions, tagged by staff type." },
  { key: "positions",       label: "Positions",       desc: "Job titles available inside each department."             },
  { key: "grade-levels",    label: "Grade Levels",    desc: "The year levels your school offers, in order."            },
  { key: "sections",        label: "Sections",        desc: "Class sections inside each grade level + adviser."        },
  { key: "subjects",        label: "Subjects",        desc: "Subjects offered, grouped by grade level and department." },
  { key: "rooms",           label: "Rooms",           desc: "Physical classrooms, labs, and venues — used by sections and class offerings." },
  { key: "class-offerings", label: "Class Offerings", desc: "Teacher × subject × section assignments per semester."    },
  { key: "sports",          label: "Sports",          desc: "Extracurricular programs and their coaches."              },
];

/* ---------- Group definitions ----------
 * Bundles the 10 admin tabs into 4 mental buckets. A flat 10-tab pill row was
 * overwhelming; this matches the actual mental model:
 *   Calendar      — set up once a year (when does school run)
 *   Organization  — set up once a year (how is the school structured)
 *   Curriculum    — set up each term (what is taught and to whom)
 *   Offerings     — touched weekly (who teaches what, when) + extracurriculars
 * Order = setup-frequency (rarest first). The leaf tab order inside each
 * group is dependency-aware (you need School Years before Semesters, etc.).
 */
const GROUPS = [
  {
    key: "calendar",
    label: "Calendar",
    desc: "Configure the academic year and the terms inside it.",
    accent: "calendar",
    items: ["school-years", "semesters"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <path d="M16 3v4M8 3v4M3 10h18" />
      </svg>
    ),
  },
  {
    key: "organization",
    label: "Organization",
    desc: "Departments and the positions within them.",
    accent: "organization",
    items: ["departments", "positions"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M5 21V7l7-4 7 4v14" />
        <path d="M9 21v-6h6v6M9 11h.01M15 11h.01" />
      </svg>
    ),
  },
  {
    key: "curriculum",
    label: "Curriculum",
    desc: "Grade levels, sections, subjects, and the rooms they meet in.",
    accent: "curriculum",
    items: ["grade-levels", "sections", "subjects", "rooms"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 4h12a4 4 0 0 1 4 4v12H8a4 4 0 0 1-4-4z" />
        <path d="M4 16a4 4 0 0 1 4-4h12" />
      </svg>
    ),
  },
  {
    key: "offerings",
    label: "Offerings",
    desc: "Teaching assignments and extracurricular programs.",
    accent: "offerings",
    items: ["class-offerings", "sports"],
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 10 12 5l10 5-10 5z" />
        <path d="M6 12v5a6 6 0 0 0 12 0v-5" />
        <path d="M22 10v6" />
      </svg>
    ),
  },
];

const GROUP_BY_ITEM = Object.fromEntries(
  GROUPS.flatMap(g => g.items.map(itemKey => [itemKey, g]))
);
const TAB_BY_KEY = Object.fromEntries(TABS.map(t => [t.key, t]));

/* ---------- Staff Type badge ---------- */
// Maps the wire-format enum to a user-facing label + a CSS class. Used by
// the renderCell special-case for the Departments table's staffType column.
const STAFF_TYPE_META = {
  MANAGEMENT:   { label: "Management",   cls: "stbManagement"  },
  TEACHING:     { label: "Teaching",     cls: "stbTeaching"    },
  NON_TEACHING: { label: "Non-Teaching", cls: "stbNonTeaching" },
  SUPPORT:      { label: "Support",      cls: "stbSupport"     },
};

function StaffTypeBadge({ value }) {
  const meta = STAFF_TYPE_META[value];
  // Unknown/legacy values still render — pass the raw token through so
  // admins can see exactly what's in the DB and fix it.
  return (
    <span className={`${s.staffTypeBadge} ${s[meta?.cls || "stbUnknown"]}`}>
      {meta ? meta.label : value}
    </span>
  );
}

/* ---------- External lookups (loaded from non-tab endpoints) ---------- */
// `filter` runs client-side after fetching the full list.
const EXTERNALS = {
  teachers: {
    endpoint: "/api/employees?all=true",
    label: "name",
    // Teaching + Management staff can be assigned as teacher / adviser /
    // coach / faculty head. "TEACHER" kept for legacy records.
    filter: r => ["TEACHING", "TEACHER", "MANAGEMENT"].includes(r.staffType),
  },
  staff: {
    endpoint: "/api/employees?all=true",
    label: "name",
  },
};

/* ---------- Field schemas ---------- */
// Each tab declares the columns it shows in the table AND the fields its
// create/edit form renders. Keeps the page driven by data instead of 7 copies.
const SCHEMA = {
  "school-years": {
    // Active column is read-only here; current term is set via the
    // "Current Term" card above the tab bar, not per-row.
    endpoint: "/api/school-years",
    columns:  [["label","Label"], ["startDate","Start"], ["endDate","End"], ["active","Active"]],
    fields:   [
      { key: "label",     label: "Label",      type: "text",     placeholder: "2026-2027", required: true },
      { key: "startDate", label: "Start date", type: "date" },
      { key: "endDate",   label: "End date",   type: "date" },
    ],
    blank: { label: "", startDate: "", endDate: "" },
  },
  "semesters": {
    endpoint: "/api/semesters",
    columns:  [["label","Label"], ["schoolYearId","School Year"], ["startDate","Start"], ["endDate","End"], ["active","Active"]],
    fields:   [
      { key: "label",        label: "Label",     type: "select",  required: true,
        options: [["1st","1st Semester"], ["2nd","2nd Semester"]] },
      { key: "schoolYearId", label: "School Year", type: "ref", refTab: "school-years", refLabel: "label", required: true },
      { key: "startDate",    label: "Start date", type: "date" },
      { key: "endDate",      label: "End date",   type: "date" },
    ],
    blank: { label: "1st", schoolYearId: "", startDate: "", endDate: "" },
  },
  "departments": {
    endpoint: "/api/departments",
    columns:  [["name","Name"], ["staffType","Staff Type"], ["headStaffId","Head"]],
    fields:   [
      { key: "name",        label: "Name",        type: "text", required: true,
        placeholder: "Administration" },
      { key: "description", label: "Description", type: "text",
        placeholder: "(optional)" },
      // The kind of staff this department employs. Drives the Staff Type
      // → Department cascade in the Employee form. Single-valued: if a
      // department legitimately mixes types (e.g. an Admin office that
      // has both a Principal and a Registrar), split it into two
      // departments. The legacy `roleNames` CSV is no longer edited from
      // the UI — it stays in the DB as a backward-compat read-only field.
      { key: "staffType",   label: "Staff type", type: "select", required: true,
        options: [
          ["",             "— Select a type —"],
          ["MANAGEMENT",   "Management"],
          ["TEACHING",     "Teaching"],
          ["NON_TEACHING", "Non-Teaching"],
          ["SUPPORT",      "Support Staff"],
        ] },
      { key: "headStaffId", label: "Head of department", type: "ext", source: "staff" },
    ],
    blank: { name: "", description: "", staffType: "", headStaffId: "" },
  },
  "positions": {
    endpoint: "/api/positions",
    columns:  [["name","Title"], ["departmentId","Department"], ["description","Description"]],
    fields:   [
      { key: "name",         label: "Position title", type: "text", required: true,
        placeholder: "Principal" },
      // Drives the Department → Role cascade in the Employee form: pick a
      // department, see only its positions in the Role dropdown. The
      // department's staffType is what determines if this role appears in
      // a given Staff Type's filtered list — so Position.staffType is no
      // longer edited here (it's still in the DB as a legacy column for
      // back-compat reads, but the new cascade ignores it).
      { key: "departmentId", label: "Department", type: "ref", required: true,
        refTab: "departments", refLabel: "name" },
      { key: "description",  label: "Description",    type: "text",
        placeholder: "(optional)" },
    ],
    blank: { name: "", description: "", departmentId: "" },
  },
  "grade-levels": {
    endpoint: "/api/grade-levels",
    columns:  [["ordering","#"], ["name","Name"], ["departmentId","Department"]],
    fields:   [
      { key: "name",         label: "Name",     type: "text", required: true, placeholder: "Grade 7" },
      // Limit picker to TEACHING departments — academic levels live there
      // since the Faculty→Department merge.
      { key: "departmentId", label: "Department", type: "ref",
        refTab: "departments", refLabel: "name",
        refFilter: r => r.staffType === "TEACHING" },
      { key: "ordering",     label: "Sort order", type: "number", placeholder: "0" },
    ],
    blank: { name: "", departmentId: "", ordering: 0 },
  },
  "sections": {
    endpoint: "/api/sections",
    columns:  [["name","Name"], ["gradeLevelId","Grade Level"], ["schoolYearId","School Year"], ["adviserStaffId","Adviser"], ["homeroomId","Homeroom"]],
    fields:   [
      { key: "name",           label: "Name", type: "text", required: true, placeholder: "St. Therese" },
      { key: "gradeLevelId",   label: "Grade Level", type: "ref", refTab: "grade-levels", refLabel: "name", required: true },
      { key: "schoolYearId",   label: "School Year", type: "ref", refTab: "school-years", refLabel: "label", required: true },
      { key: "adviserStaffId", label: "Adviser", type: "ext", source: "teachers" },
      { key: "homeroomId",     label: "Homeroom", type: "room", placeholder: "Pick a room…" },
    ],
    blank: { name: "", gradeLevelId: "", schoolYearId: "", adviserStaffId: "", homeroomId: "" },
  },
  // Rooms is rendered by the custom RoomsPanel which manages its own
  // state — this entry exists only so the schema-driven loadTab effect
  // doesn't crash when the user lands on the Rooms tab.
  "rooms": {
    endpoint: "/api/rooms",
    columns:  [["name","Name"], ["building","Building"], ["floor","Floor"], ["capacity","Capacity"], ["roomType","Type"]],
    fields:   [
      { key: "name",     label: "Name",     type: "text",   required: true, placeholder: "Rm 201" },
      { key: "building", label: "Building", type: "text",   placeholder: "Main" },
      { key: "floor",    label: "Floor",    type: "text",   placeholder: "2" },
      { key: "capacity", label: "Capacity", type: "number", placeholder: "40" },
      { key: "roomType", label: "Type",     type: "text",   placeholder: "STANDARD" },
    ],
    blank: { name: "", building: "", floor: "", capacity: "", roomType: "STANDARD" },
  },
  "subjects": {
    endpoint: "/api/subjects",
    columns:  [["code","Code"], ["title","Title"], ["units","Units"], ["gradeLevelId","Grade Level"], ["departmentId","Department"]],
    fields:   [
      { key: "code",         label: "Code",  type: "text", required: true, placeholder: "MATH7" },
      { key: "title",        label: "Title", type: "text", required: true, placeholder: "Mathematics 7" },
      { key: "units",        label: "Units", type: "number", placeholder: "1.0" },
      { key: "gradeLevelId", label: "Grade Level", type: "ref", refTab: "grade-levels", refLabel: "name" },
      // Limit picker to TEACHING departments — same merge as grade-levels.
      { key: "departmentId", label: "Department",  type: "ref",
        refTab: "departments", refLabel: "name",
        refFilter: r => r.staffType === "TEACHING" },
    ],
    blank: { code: "", title: "", units: "", gradeLevelId: "", departmentId: "" },
  },
  "class-offerings": {
    endpoint: "/api/class-offerings",
    columns:  [
      ["semesterId",     "Semester"],
      ["sectionId",      "Section"],
      ["subjectId",      "Subject"],
      ["teacherStaffId", "Teacher"],
      ["schedule",       "Schedule"],
      // Display column reads the new roomId (preferred) with a fallback
      // to the legacy text `room` — handled by renderCell below.
      ["roomId",         "Room"],
    ],
    fields: [
      { key: "semesterId",     label: "Semester",  type: "ref", refTab: "semesters", refLabel: "label", required: true },
      { key: "sectionId",      label: "Section",   type: "ref", refTab: "sections",  refLabel: "name",  required: true },
      { key: "subjectId",      label: "Subject",   type: "ref", refTab: "subjects",  refLabel: "code",  required: true },
      { key: "teacherStaffId", label: "Teacher",   type: "ext", source: "teachers" },
      { key: "schedule",       label: "Schedule",  type: "text", placeholder: "Mon/Wed 08:00-09:00" },
      // Free-text `room` is preserved server-side via schema.blank, but the
      // form picker is the new authoritative input. Legacy rows display
      // their text room until an admin reassigns via the picker.
      { key: "roomId",         label: "Room",      type: "room", placeholder: "Pick a room…" },
    ],
    blank: { semesterId: "", sectionId: "", subjectId: "", teacherStaffId: "", schedule: "", room: "", roomId: "" },
  },
  "sports": {
    endpoint: "/api/sports",
    columns:  [["name","Name"], ["season","Season"], ["coachStaffId","Coach"]],
    fields:   [
      { key: "name",         label: "Name",   type: "text", required: true, placeholder: "Basketball" },
      { key: "season",       label: "Season", type: "text", placeholder: "Wet Season" },
      // Coaches can come from any staff type — PE teacher, security guard
      // who volunteers, parent-coordinator, etc. — so we pull the full staff.
      { key: "coachStaffId", label: "Coach",  type: "ext", source: "staff" },
    ],
    blank: { name: "", season: "", coachStaffId: "" },
  },
};

/* ---------- Page ---------- */
export default function AcademicsPage() {
  // null = hub view (4 group cards). String = leaf editor for that tab.
  const [activeTab, setActiveTab] = useState(null);
  const [data, setData]           = useState({}); // { tabKey: [rows] }
  const [externals, setExternals] = useState({}); // { sourceKey: [rows] }
  const [editing, setEditing]     = useState(null); // current row being edited (or "new")
  const [form, setForm]           = useState(null);
  const [error, setError]         = useState("");
  const [busy, setBusy]           = useState(false);
  const [currentTerm, setCurrentTerm] = useState(null); // {schoolYearId, semesterId, schoolYearLabel, semesterLabel}
  const [termPickerOpen, setTermPickerOpen] = useState(false);

  // Per-tab column sort. null = no sort (use API order). Reset on tab
  // switch so sort context never bleeds between e.g. Departments and
  // Positions, which have totally different column shapes.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    loadCurrentTerm();
    // Eager-load every tab's row count so the hub cards can show real
    // numbers ("3 school years · 1 semester") on first paint. Each
    // endpoint returns a plain JSON array so the body is small. Cached
    // into the same `data` state the leaf editors already read from,
    // so navigating into a tab reuses the prefetched rows.
    TABS.forEach(t => loadTab(t.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadCurrentTerm() {
    try {
      const res = await fetch(`${API_BASE}/api/academics/current-term`);
      if (res.ok) setCurrentTerm(await res.json());
    } catch { /* non-fatal */ }
  }

  const scrollRef = useScrollActiveClass(s.scrolling);
  const schema = activeTab ? SCHEMA[activeTab] : null;
  const rows   = activeTab ? (data[activeTab] || []) : [];

  // Reset sort when switching tabs — different schemas, different columns.
  useEffect(() => {
    setSortKey(null);
    setSortDir("asc");
  }, [activeTab]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  /** Resolve a row's column value to something sortable. Mirrors
   *  renderCell's resolution so the sort order matches what the user
   *  sees on screen — refs sort by their displayed label, not the raw
   *  numeric id; booleans by 0/1; numbers as numbers; strings case-
   *  insensitively. Empty / null values return null so they bucket to
   *  the end regardless of direction. */
  function sortValueFor(row, key) {
    const field = schema?.fields.find(f => f.key === key);
    const v = row[key];
    if (v == null || v === "") return null;
    if (field?.type === "ref") {
      const refList = data[field.refTab] || [];
      const ref = refList.find(r => r.id === v);
      return (ref?.[field.refLabel] || "").toString().toLowerCase();
    }
    if (field?.type === "ext") {
      const extList = externals[field.source] || [];
      const ref = extList.find(r => r.id === v);
      return (ref?.[EXTERNALS[field.source].label] || "").toString().toLowerCase();
    }
    if (typeof v === "number")  return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    return v.toString().toLowerCase();
  }

  const sortedRows = useMemo(() => {
    // Defensive: if we've just switched tabs and sortKey points at a
    // column the new schema doesn't have, fall back to API order.
    if (!sortKey || !schema?.columns.some(([k]) => k === sortKey)) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortValueFor(a, sortKey);
      const bv = sortValueFor(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;       // empty buckets last regardless of dir
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, schema, sortKey, sortDir, data, externals]);
  const activeGroup = activeTab ? GROUP_BY_ITEM[activeTab] : null;

  /* Load rows for the active tab + any tabs/externals referenced by fields */
  useEffect(() => {
    if (!activeTab) return;                         // hub mode — nothing to refresh
    const sch = SCHEMA[activeTab];
    const tabsToLoad = new Set([activeTab]);
    const extsToLoad = new Set();
    sch.fields.forEach(f => {
      if (f.type === "ref" || f.type === "multiref") tabsToLoad.add(f.refTab);
      if (f.type === "ext") extsToLoad.add(f.source);
      // Room-typed fields need the rooms list so the table column can
      // resolve roomId → Room.name in renderCell.
      if (f.type === "room") tabsToLoad.add("rooms");
    });
    // Also resolve refs/exts that are columns but not form fields
    sch.columns.forEach(([k]) => {
      const f = sch.fields.find(ff => ff.key === k);
      if (f?.type === "ref" || f?.type === "multiref") tabsToLoad.add(f.refTab);
      if (f?.type === "ext") extsToLoad.add(f.source);
      if (f?.type === "room") tabsToLoad.add("rooms");
    });
    tabsToLoad.forEach(loadTab);
    extsToLoad.forEach(loadExternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function loadTab(tabKey) {
    try {
      const res = await fetch(`${API_BASE}${SCHEMA[tabKey].endpoint}`);
      if (!res.ok) throw new Error(`Load ${tabKey} failed`);
      const list = await res.json();
      setData(prev => ({ ...prev, [tabKey]: list }));
    } catch (e) {
      setError(e.message);
    }
  }

  async function loadExternal(sourceKey) {
    if (externals[sourceKey]) return;
    try {
      const def = EXTERNALS[sourceKey];
      const res = await fetch(`${API_BASE}${def.endpoint}`);
      if (!res.ok) throw new Error(`Load ${sourceKey} failed`);
      let list = await res.json();
      if (def.filter) list = list.filter(def.filter);
      setExternals(prev => ({ ...prev, [sourceKey]: list }));
    } catch (e) {
      setError(e.message);
    }
  }

  function startCreate(preset) {
    // Guard: React passes a SyntheticEvent when used directly as onClick;
    // only treat `preset` as a field override when it's a plain object.
    const usable = preset && typeof preset === "object" && !preset.nativeEvent && !preset.target
      ? preset
      : null;
    setEditing("new");
    setForm({ ...schema.blank, ...(usable || {}) });
    setError("");
  }
  function startEdit(row) {
    setEditing(row);
    setForm({ ...schema.blank, ...row });
    setError("");
  }
  function cancel() {
    setEditing(null);
    setForm(null);
    setError("");
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const isNew = editing === "new";
      const url   = isNew ? `${API_BASE}${schema.endpoint}` : `${API_BASE}${schema.endpoint}/${editing.id}`;
      const payload = { ...form };
      // strip empty strings -> null for numeric/ref/ext/room fields
      schema.fields.forEach(f => {
        if (f.type === "number" || f.type === "ref" || f.type === "ext" || f.type === "room") {
          if (payload[f.key] === "" || payload[f.key] === undefined) payload[f.key] = null;
          else if (payload[f.key] !== null) payload[f.key] = Number(payload[f.key]);
        }
        if (f.type === "date" && payload[f.key] === "") payload[f.key] = null;
      });
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      await loadTab(activeTab);
      cancel();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(row) {
    if (!window.confirm(`Delete "${rowDisplayName(row)}"?`)) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}${schema.endpoint}/${row.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await loadTab(activeTab);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function rowDisplayName(row) {
    return row.label || row.name || row.title || row.code || `#${row.id}`;
  }

  function renderCell(row, key) {
    const field = schema.fields.find(f => f.key === key);
    // Special case: legacy class-offering rows may have roomId=null but
    // a non-empty text `room` from before the migration ran. Render the
    // text fallback so admins still see something meaningful.
    if (field?.type === "room") {
      const id = row[key];
      if (id != null && id !== "") {
        const list = data["rooms"] || [];
        const ref = list.find(r => r.id === id);
        if (ref) return ref.name || `#${id}`;
        return `#${id}`;
      }
      // Fallback to legacy text room (only present on class-offerings).
      if (row.room) return row.room;
      return "—";
    }
    const v = row[key];
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "✓" : "—";
    // Premium staffType pill — only on the Departments table where the
    // column key matches. Other tabs that happen to expose a staffType
    // field still render plainly (no current consumers, but defensive).
    if (key === "staffType" && schema.endpoint === "/api/departments") {
      return <StaffTypeBadge value={v} />;
    }
    if (field?.type === "ref") {
      const refList = data[field.refTab] || [];
      const ref = refList.find(r => r.id === v);
      return ref ? ref[field.refLabel] : `#${v}`;
    }
    if (field?.type === "ext") {
      const extList = externals[field.source] || [];
      const ref = extList.find(r => r.id === v);
      return ref ? ref[EXTERNALS[field.source].label] : `#${v}`;
    }
    return String(v);
  }

  return (
    <div className={s.page}>
      {/* ── Page header ── */}
      <div className={s.header}>
        <div>
          <div className={s.eyebrow}>Admin · Academics</div>
          <h1 className={s.title}>School Structure</h1>
          <p className={s.sub}>
            {activeTab
              ? TAB_BY_KEY[activeTab]?.desc
              : "Set up the school year, faculties, curriculum and class offerings — grouped to keep things tidy."}
          </p>
        </div>
      </div>

      {/* ── Current term banner (always visible) ── */}
      <div className={s.termCard}>
        <div>
          <div className={s.termCardLabel}>Current Term</div>
          {currentTerm && currentTerm.schoolYearId ? (
            <div className={s.termCardValue}>
              {currentTerm.schoolYearLabel} · {formatSemesterLabel(currentTerm.semesterLabel)}
            </div>
          ) : (
            <div className={`${s.termCardValue} ${s.termCardEmpty}`}>
              No current term set — pick a school year and semester
            </div>
          )}
        </div>
        <button className={s.btnPrimary} onClick={() => setTermPickerOpen(true)}>
          Change
        </button>
      </div>

      {termPickerOpen && (
        <CurrentTermPicker
          current={currentTerm}
          onClose={() => setTermPickerOpen(false)}
          onSaved={async (next) => {
            setCurrentTerm(next);
            setTermPickerOpen(false);
            if (data["school-years"]) loadTab("school-years");
            if (data["semesters"])    loadTab("semesters");
          }}
        />
      )}

      {/* ── Hub view (no item selected): 4 group cards ───────────────── */}
      {!activeTab && (
        <div className={s.hub}>
          {GROUPS.map((g, i) => {
            return (
              <button
                key={g.key}
                className={`${s.hubCard} ${s[`hubAccent_${g.accent}`]}`}
                style={{ animationDelay: `${i * 80}ms` }}
                onClick={() => { setActiveTab(g.items[0]); cancel(); }}>
                <div className={s.hubCardSheen} aria-hidden="true" />
                <div className={s.hubIcon}>{g.icon}</div>
                <div className={s.hubLabel}>{g.label}</div>
                <div className={s.hubDesc}>{g.desc}</div>
                <div className={s.hubChips}>
                  {g.items.map(itemKey => {
                    const count = data[itemKey]?.length;
                    const label = TAB_BY_KEY[itemKey].label;
                    return (
                      <span key={itemKey} className={s.hubChip}>
                        <span className={s.hubChipNum}>{count ?? "—"}</span>
                        <span className={s.hubChipLabel}>{label}</span>
                      </span>
                    );
                  })}
                </div>
                <div className={s.hubCta}>
                  Open
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.4"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Detail view (item selected): sidebar + content ───────────── */}
      {activeTab && (
        <div className={s.detail}>
          <aside className={s.navColumn}>
            <button
              className={s.backToHub}
              onClick={() => { setActiveTab(null); cancel(); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              All groups
            </button>

            {GROUPS.map(g => {
              const isCurrentGroup = g.key === activeGroup?.key;
              return (
                <div key={g.key} className={s.navGroup}>
                  <div className={`${s.navGroupHead} ${isCurrentGroup ? s.navGroupHeadActive : ""}`}>
                    <span className={`${s.navGroupIcon} ${s[`hubAccent_${g.accent}`]}`}>
                      {g.icon}
                    </span>
                    <span className={s.navGroupLabel}>{g.label}</span>
                  </div>
                  <div className={s.navList}>
                    {g.items.map(itemKey => {
                      const tab = TAB_BY_KEY[itemKey];
                      const active = activeTab === itemKey;
                      const count  = data[itemKey]?.length;
                      return (
                        <button
                          key={itemKey}
                          className={`${s.navItem} ${active ? s.navItemActive : ""}`}
                          onClick={() => { setActiveTab(itemKey); cancel(); }}>
                          <span className={s.navItemLabel}>{tab.label}</span>
                          {count != null && (
                            <span className={s.navItemCount}>{count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </aside>

          <div className={s.contentColumn}>
            <div className={s.crumb}>
              <button
                className={s.crumbLink}
                onClick={() => { setActiveTab(null); cancel(); }}>
                Academics
              </button>
              <span className={s.crumbSep}>›</span>
              <span className={s.crumbGroup}>{activeGroup?.label}</span>
              <span className={s.crumbSep}>›</span>
              <span className={s.crumbCurrent}>{TAB_BY_KEY[activeTab].label}</span>
            </div>

            <div className={s.card} key={activeTab /* re-mount for fade */}>
              <div className={s.cardHead}>
                <div>
                  <h2 className={s.cardTitle}>{TAB_BY_KEY[activeTab].label}</h2>
                  <div className={s.cardSub}>{TAB_BY_KEY[activeTab].desc}</div>
                </div>
                {!editing && activeTab !== "class-offerings" && (
                  <button className={s.btnPrimary} onClick={() => startCreate()}>+ New</button>
                )}
              </div>

              {error && <div className={s.error}>{error}</div>}

              {activeTab === "class-offerings" ? (
          // Class Offerings gets its own teacher-grouped panel with a batch
          // add modal. The generic table would show one row per (teacher,
          // subject, section) — too noisy for the real use case.
          <ClassOfferingsPanel />
        ) : activeTab === "rooms" ? (
          // Rooms is fully self-contained — its own list, its own edit
          // modal, its own delete-confirm. The generic table render below
          // is bypassed.
          <RoomsPanel />
        ) : activeTab === "subjects" ? (
          <SubjectsByGradePanel
            subjects={data["subjects"] || []}
            gradeLevels={data["grade-levels"] || []}
            // Post Faculty→Department merge: the panel still labels its
            // groupings "faculties" (the role they play in the UI), but
            // the rows are TEACHING-tagged departments now.
            faculties={(data["departments"] || []).filter(d => d.staffType === "TEACHING")}
            onEdit={startEdit}
            onDelete={remove}
            onAfterCreate={() => loadTab("subjects")}
          />
        ) : activeTab === "sections" ? (
          <SectionsByGradePanel
            sections={data["sections"] || []}
            gradeLevels={data["grade-levels"] || []}
            schoolYears={data["school-years"] || []}
            employees={externals["teachers"] || []}
            onEdit={startEdit}
            onDelete={remove}
            onAfterCreate={() => loadTab("sections")}
          />
        ) : (
              <div className={s.tableWrap} ref={scrollRef}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      {schema.columns.map(([k, label]) => {
                        const active = sortKey === k;
                        return (
                          <th
                            key={k}
                            className={s.thSortable}
                            onClick={() => toggleSort(k)}
                            onKeyDown={e => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleSort(k);
                              }
                            }}
                            tabIndex={0}
                            aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                          >
                            <span className={s.thSortableInner}>
                              {label}
                              <span className={`${s.sortArrow} ${active ? s.sortArrowActive : ""}`}>
                                {active ? (sortDir === "asc" ? "▴" : "▾") : "↕"}
                              </span>
                            </span>
                          </th>
                        );
                      })}
                      <th className={s.actionsCol}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr><td colSpan={schema.columns.length + 1} className={s.emptyRow}>No records yet.</td></tr>
                    ) : sortedRows.map(row => (
                      <tr key={row.id}>
                        {schema.columns.map(([k]) => <td key={k}>{renderCell(row, k)}</td>)}
                        <td className={s.actionsCol}>
                          <button className={s.btnGhost} onClick={() => startEdit(row)}>Edit</button>
                          <button className={s.btnDanger} onClick={() => remove(row)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* ── Edit / Create modal ───────────────────────────────────────── */}
      {/* Click-outside intentionally does NOT cancel — form data could be lost.
          Admins dismiss only via the Cancel button or the × close control.

          Portaled to <body> so position:fixed centers on the viewport
          (escapes .shell's transform that would otherwise trap the
          fixed-positioning containing block — modal would land off-screen
          when scrolled). */}
      {editing && createPortal(
        <div className={s.formOverlay}>
          <form onSubmit={save} className={s.formModal}>
            <div className={s.formModalHead}>
              <h3 className={s.formModalTitle}>
                {editing === "new" ? "New " : "Edit "}
                {singularLabel(TABS.find(t => t.key === activeTab)?.label || "")}
              </h3>
              <button
                type="button"
                className={s.closeBtn}
                onClick={cancel}
                disabled={busy}
                aria-label="Close">✕</button>
            </div>

            {error && <div className={s.error}>{error}</div>}

            <div className={s.formModalBody}>
              <div className={s.form}>
                {schema.fields.map(f => (
                  <FormField
                    key={f.key}
                    field={f}
                    value={form[f.key]}
                    onChange={v => setForm(prev => ({ ...prev, [f.key]: v }))}
                    refData={(f.type === "ref" || f.type === "multiref") ? data[f.refTab] : null}
                    extData={f.type === "ext" ? externals[f.source] : null}
                  />
                ))}
              </div>
            </div>

            <div className={s.formModalFoot}>
              <button type="button" className={s.btnGhost} onClick={cancel} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className={s.btnPrimary} disabled={busy}>
                {busy ? "Saving..." : (editing === "new" ? "Create" : "Save changes")}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}

/** Tabs use plural labels ("School Years", "Faculties"). For modal titles we
 *  want the singular form ("School Year", "Faculty"). Rules cover the labels
 *  currently in TABS — extend here if new tabs use unusual plurals. */
function singularLabel(label) {
  if (label.endsWith("ies")) return label.slice(0, -3) + "y";
  if (label.endsWith("s"))   return label.slice(0, -1);
  return label;
}

/* ---------- Form field ---------- */
function FormField({ field, value, onChange, refData, extData }) {
  if (field.type === "checkbox") {
    return (
      <label className={s.checkboxRow}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
        />
        <span>{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    // Schema may declare a leading [""] placeholder option (e.g. Department
    // staffType). Surface that label as the trigger placeholder and exclude
    // the empty entry from the option list.
    const all          = field.options.map(([v, lbl]) => ({ value: v, label: lbl }));
    const placeholderEntry = all.find(o => !o.value);
    const realOptions      = all.filter(o => o.value);
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <Select
          value={value ?? ""}
          onChange={onChange}
          options={realOptions}
          placeholder={placeholderEntry?.label || "— Select —"}
          ariaLabel={field.label}
        />
      </div>
    );
  }
  if (field.type === "ref") {
    // Optional refFilter narrows the dropdown without changing the underlying
    // refTab fetch (e.g. show only TEACHING departments in the Subject /
    // Grade Level pickers post-Faculty-merge). The full list is still in
    // memory so renderCell can resolve any id, even ones excluded here.
    const fullList = refData || [];
    const list = field.refFilter ? fullList.filter(field.refFilter) : fullList;
    // String-coerce on both sides so the trigger reflects the current value
    // even when the form state holds a numeric id from the server.
    const options = [
      // Optional fields get an explicit "None" so admins can clear an
      // existing pick. Required fields do not — the form's required check
      // catches an empty value before save.
      ...(field.required ? [] : [{ value: "", label: "— None —" }]),
      ...list.map(r => ({
        value: String(r.id),
        label: r[field.refLabel] || `#${r.id}`,
      })),
    ];
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <Select
          value={value == null ? "" : String(value)}
          onChange={onChange}
          options={options}
          placeholder="— Select —"
          ariaLabel={field.label}
        />
      </div>
    );
  }
  if (field.type === "multiref") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <MultiRefDropdown
          options={refData || []}
          labelKey={field.refLabel}
          value={value || ""}
          onChange={onChange}
          placeholder={field.placeholder}
        />
      </div>
    );
  }
  if (field.type === "ext") {
    const list = extData || [];
    const labelKey = EXTERNALS[field.source].label;
    const options = [
      // External lookups (Heads, Advisers, etc.) are usually optional —
      // mirror the ref field's "None" affordance so admins can unassign.
      ...(field.required ? [] : [{ value: "", label: "— None —" }]),
      ...list.map(r => ({
        value: String(r.id),
        label: r[labelKey] || `#${r.id}`,
      })),
    ];
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <Select
          value={value == null ? "" : String(value)}
          onChange={onChange}
          options={options}
          placeholder="— Select —"
          ariaLabel={field.label}
        />
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <DatePicker
          value={value || ""}
          onChange={v => onChange(v)}
          placeholder={field.placeholder || "MM/DD/YYYY"}
        />
      </div>
    );
  }
  if (field.type === "room") {
    // Picker maps null/empty to no-selection, otherwise expects a numeric
    // roomId. Form state may carry a string from form.blank — coerce.
    const numericValue = value === "" || value == null ? null : Number(value);
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <RoomPicker
          value={numericValue}
          onChange={(roomId) => onChange(roomId == null ? "" : roomId)}
          required={field.required}
          placeholder={field.placeholder || "Pick a room…"}
        />
      </div>
    );
  }
  return (
    <div className={s.field}>
      <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
      <input
        className={s.input}
        type={field.type}
        value={value ?? ""}
        placeholder={field.placeholder || ""}
        onChange={e => onChange(e.target.value)}
        required={field.required}
      />
    </div>
  );
}

/** Multi-select dropdown for the `multiref` field type.
 *  - `value` is a CSV string of label values (e.g. "Principal, Dean").
 *  - `onChange` receives the new CSV string.
 *  - `options` is the list of rows from the linked tab (e.g. positions);
 *    `labelKey` is the field on each row to display + store (e.g. "name").
 *
 *  Stores names rather than ids on purpose: the backend's roleNames column
 *  is a CSV of names that the Role ↔ Department cascade compares against
 *  Position.name, so id-based storage would force a join everywhere it's
 *  read. The downside is that renaming a Position silently orphans the
 *  CSV entries — that's a known limitation of the current data model.
 *
 *  Renders as a button styled like .input that opens a checkbox panel
 *  below. Closes on outside click or Escape. */
function MultiRefDropdown({ options, labelKey, value, onChange, placeholder }) {
  const [open, setOpen]       = useState(false);
  // Position is computed from the trigger button's bounding rect and stored
  // here so the portaled popover knows where to render. Recomputed on scroll
  // and resize so it stays glued to the trigger as the modal body scrolls.
  const [pos, setPos]         = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const popRef     = useRef(null);

  // Parse the stored CSV once per render. Trimmed + de-duped so ", , Dean"
  // doesn't make Dean look unselected because of an extra space.
  const selectedSet = new Set(
    (value || "")
      .split(",")
      .map(x => x.trim())
      .filter(Boolean)
  );

  // Compute popover position from the trigger's bounding rect. Pulled out
  // so the open-handler, scroll listener, and resize listener can all share
  // the same logic.
  function reposition() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  }

  // useLayoutEffect runs before paint so the popover never flashes at
  // {0,0} on first open — it lands at its anchored position immediately.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open]);

  // Outside-click + Escape close. Mousedown not click — clicking inside a
  // checkbox dispatches click AFTER mousedown lands on the document,
  // making the panel close before the toggle registers. Outside-click
  // checks BOTH the trigger and the portaled popover (the popover is no
  // longer a DOM descendant of the trigger's wrapper).
  // Scroll + resize also re-anchor the popover so it follows the trigger
  // when the modal body scrolls. capture=true so we catch scrolls inside
  // .formModalBody (the actual scrolling container, not window).
  useEffect(() => {
    if (!open) return;
    const onMouse = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (popRef.current?.contains(t))     return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScrollOrResize = () => reposition();
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  function toggle(name) {
    const next = new Set(selectedSet);
    if (next.has(name)) next.delete(name); else next.add(name);
    // Re-serialize in the order options arrived from the API so the stored
    // CSV is stable across edits (instead of "last clicked first").
    const ordered = options.map(o => o[labelKey]).filter(n => next.has(n));
    onChange(ordered.join(", "));
  }
  function selectAll()   { onChange(options.map(o => o[labelKey]).filter(Boolean).join(", ")); }
  function clearAll()    { onChange(""); }

  // Trigger summary: show the chosen names truncated, OR the placeholder
  // when nothing is selected (which means "any role allowed" downstream).
  const selectedNames = options
    .map(o => o[labelKey])
    .filter(n => selectedSet.has(n));
  let summary;
  if (selectedNames.length === 0) {
    summary = <span style={{ color: "#999" }}>{placeholder || "— none selected —"}</span>;
  } else if (selectedNames.length <= 3) {
    summary = selectedNames.join(", ");
  } else {
    summary = `${selectedNames.slice(0, 2).join(", ")} + ${selectedNames.length - 2} more`;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={s.input}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          textAlign: "left", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 8, cursor: "pointer",
          width: "100%", boxSizing: "border-box",
        }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </span>
        <span aria-hidden="true" style={{
          display: "inline-block", width: 8, height: 8,
          borderRight: "2px solid #6b5a3a", borderBottom: "2px solid #6b5a3a",
          transform: open ? "rotate(-135deg) translate(-1px,-1px)" : "rotate(45deg) translate(-2px,-2px)",
          transition: "transform 0.15s ease",
        }} />
      </button>

      {/* Portaled to document.body so the popover can escape the modal's
          overflow:auto without getting clipped. Position is computed from
          the trigger's bounding rect and re-anchored on scroll/resize. */}
      {open && createPortal(
        <div
          ref={popRef}
          role="listbox"
          style={{
            position: "fixed",
            top: pos.top, left: pos.left, width: pos.width,
            background: "#fff", border: "1px solid #d6cdb8", borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            // Above the modal backdrop (typical modals are ~1000-2000).
            // 9999 is safe and matches the convention other portaled
            // overlays in similar codebases use.
            zIndex: 9999,
            maxHeight: 280, overflowY: "auto", padding: 6,
          }}>
          {/* Quick actions row — useful when there are many options. */}
          <div style={{ display: "flex", gap: 6, padding: "4px 6px 8px",
                        borderBottom: "1px solid #efe7d6", marginBottom: 4 }}>
            <button type="button" onClick={selectAll}
              style={miniActionStyle}>Select all</button>
            <button type="button" onClick={clearAll}
              style={miniActionStyle}>Clear</button>
            <span style={{ marginLeft: "auto", fontSize: "0.72rem", color: "#8a8070",
                           alignSelf: "center" }}>
              {selectedSet.size} of {options.length}
            </span>
          </div>
          {options.length === 0 ? (
            <div style={{ padding: "12px 8px", fontSize: "0.82rem",
                          color: "#8a8070", fontStyle: "italic" }}>
              No options available. Add Positions first under the Positions tab.
            </div>
          ) : options.map(opt => {
            const name = opt[labelKey];
            if (!name) return null;
            const checked = selectedSet.has(name);
            return (
              <label key={opt.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 8px", borderRadius: 6, cursor: "pointer",
                fontSize: "0.85rem", color: "#3d342a",
                background: checked ? "#fdf6e0" : "transparent",
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(name)}
                />
                <span>{name}</span>
                {opt.staffType && (
                  <span style={{
                    marginLeft: "auto", fontSize: "0.66rem", fontWeight: 700,
                    letterSpacing: 0.4, textTransform: "uppercase",
                    background: "#f0ebe0", color: "#8a6a1c",
                    padding: "1px 6px", borderRadius: 4,
                  }}>
                    {opt.staffType}
                  </span>
                )}
              </label>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

const miniActionStyle = {
  fontSize: "0.72rem", padding: "3px 8px", borderRadius: 6,
  background: "#fff", color: "#6b5a3a",
  border: "1px solid #d6cdb8", cursor: "pointer",
};

/* ---------- Current Term picker modal ----------
 * Single source of truth for "which term is active." Replaces the per-tab
 * Active checkboxes that let admins get School Year and Semester out of sync.
 * Hits POST /api/academics/current-term which flips both flags atomically. */
function CurrentTermPicker({ current, onClose, onSaved }) {
  const [years, setYears]       = useState([]);
  const [semesters, setSems]    = useState([]);
  const [syId, setSyId]         = useState(current?.schoolYearId ? String(current.schoolYearId) : "");
  const [semId, setSemId]       = useState(current?.semesterId   ? String(current.semesterId)   : "");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
    ]).then(([sy, sem]) => {
      setYears(sy);
      setSems(sem);
    }).catch(e => setError(e.message));
  }, []);

  const semestersInYear = semesters.filter(x => String(x.schoolYearId) === syId);

  // If the selected semester doesn't belong to the selected SY, clear it.
  useEffect(() => {
    if (semId && !semestersInYear.some(x => String(x.id) === semId)) setSemId("");
  }, [syId, semesters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes the modal (outside-click no longer does).
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    if (!syId || !semId) { setError("Pick a school year and a semester."); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/academics/current-term`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schoolYearId: Number(syId), semesterId: Number(semId) }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      onSaved(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Portaled to <body> so the fixed-position overlay centers on the
  // viewport rather than on .shell (whose transform would otherwise
  // trap fixed positioning).
  return createPortal(
    <div className={s.overlay}>
      <form className={s.modal} onClick={e => e.stopPropagation()} onSubmit={submit}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Set Current Term</h3>
          <button type="button" className={s.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.modalBody}>
          {error && <div className={s.error}>{error}</div>}

          <div className={s.field}>
            <label className={s.label}>School Year <span className={s.req}>*</span></label>
            <select className={s.input} value={syId} onChange={e => setSyId(e.target.value)} required>
              <option value="">— select —</option>
              {years.map(y => <option key={y.id} value={y.id}>{y.label}</option>)}
            </select>
          </div>

          <div className={s.field}>
            <label className={s.label}>Semester <span className={s.req}>*</span></label>
            <select className={s.input} value={semId} onChange={e => setSemId(e.target.value)} required disabled={!syId}>
              <option value="">— select —</option>
              {semestersInYear.map(x => (
                <option key={x.id} value={x.id}>{formatSemesterLabel(x.label)}</option>
              ))}
            </select>
            {syId && semestersInYear.length === 0 && (
              <div style={{ fontSize: "0.78rem", color: "#a38b6a" }}>
                No semesters yet for this school year — add one on the Semesters tab first.
              </div>
            )}
          </div>
        </div>

        <div className={s.modalFoot}>
          <button type="button" className={s.btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" className={s.btnPrimary} disabled={busy}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

function formatSemesterLabel(raw) {
  if (!raw) return "—";
  if (raw === "1st") return "1st Semester";
  if (raw === "2nd") return "2nd Semester";
  return raw;
}
